import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiError, EnvironmentCheckService } from '@fleet-beat/core';
import { Observable } from 'rxjs';
import { ResponseDto } from '../models';
import { HttpRequestOptions } from '../models/http-request-options.model';

type ParamsType = string | number | object | string[] | number[] | boolean;

@Injectable({
  providedIn: 'root',
})
export class BaseApiService {
  private readonly defaultErrorMessage: string = 'Unknown server error. Please try later.';

  constructor(private http: HttpClient, private environmentCheckService: EnvironmentCheckService) {}

  get<T>(url: string, options?: HttpRequestOptions): Observable<T> {
    return this.http.get<T>(url, this.prepareOptions(options));
  }

  post<T1, T2>(url: string, body: T1, options?: HttpRequestOptions): Observable<T2> {
    return this.http.post<T2>(url, body, this.prepareOptions(options));
  }

  put<T1, T2>(url: string, body: T1, options?: HttpRequestOptions): Observable<T2> {
    return this.http.put<T2>(url, body, this.prepareOptions(options));
  }

  patch<T1, T2>(url: string, body: T1, options?: HttpRequestOptions): Observable<T2> {
    return this.http.patch<T2>(url, body, this.prepareOptions(options));
  }

  delete<T1>(url: string, options?: HttpRequestOptions): Observable<T1> {
    return this.http.delete<T1>(url, this.prepareOptions(options));
  }

  catchApiError(response: HttpErrorResponse & Error): never {
    let error: ResponseDto<undefined> = response.error;
    if (!error?.message?.length) {
      error = {
        error: true,
        message: this.environmentCheckService.qaDev ? response.message : this.defaultErrorMessage,
      };
    }
    return this.throwApiError(error, response.status?.toString());
  }

  mapResponse<T>(response: ResponseDto<T>): T {
    if (response.error) {
      return this.throwApiError(response as ResponseDto<undefined>);
    }
    return response.data;
  }

  mapResponseV2<T>(response: ResponseDto<T>): ResponseDto<T> {
    if (response.error) {
      return this.throwApiError(response as ResponseDto<undefined>);
    }
    return response;
  }

  private throwApiError(error: ResponseDto<undefined>, code?: string): never {
    const message: string = error?.message ? error.message : this.defaultErrorMessage;
    throw new ApiError([message], code);
  }

  private prepareOptions(options?: HttpRequestOptions): {
    headers?: HttpHeaders | Record<string, string>;
    params?: HttpParams | Record<string, string>;
    isBearer?: boolean;
  } {
    if (!options) {
      return { headers: {} };
    }

    const { headers, params, auth, ...other }: HttpRequestOptions = options;
    return {
      headers: {
        ...this.convertParams(headers),
        ...this.convertParams(
          auth
            ? {
                Authorization: auth.token ? `Bearer ${auth.token}` : undefined,
                refreshTocken: auth.refreshToken ? auth.refreshToken : undefined,
              }
            : {},
        ),
      },
      params: this.convertParams(params),
      ...other,
    };
  }

  private convertParams(params: HttpParams | HttpHeaders | Record<string, ParamsType>): Record<string, string> {
    if (!params) {
      return {};
    }

    const keys: string[] = typeof params.keys === 'function' ? params.keys() : Object.keys(params);
    return keys
      .map((key: string) => ({
        key,
        value: typeof params.get === 'function' ? params.get(key) : params[key as keyof typeof params],
      }))
      .flatMap(({ key, value }: { key: string; value: object }) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return Object.keys(value).map((subKey: string) => ({
            key: `${key}[${subKey}]`,
            value: value[subKey as keyof typeof value],
          }));
        } else {
          return [{ key, value }];
        }
      })
      .map(({ key, value }: { key: string; value: object }) => {
        if (value === null || value === undefined) {
          return null;
        } else if (Array.isArray(value)) {
          return {
            key,
            value: value
              .filter(Boolean)
              .map((item: number | string) => item.toString())
              .reduce((prev: string, curr: string) => `${prev},${curr}`),
          };
        } else {
          return { key, value: value.toString() };
        }
      })
      .filter(Boolean)
      .reduce((result: Record<string, string>, { key, value }: { key: string; value: string }) => {
        result[key] = value;
        return result;
      }, {});
  }
}
